import { vsObjectName as gamingContractName } from "./configs/gamingContract";
import {
  ContractService,
  ContractTypeService,
  vsServiceName as ContractServiceName,
} from "../../../Contract/shared/ContractService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import { Record } from "../../../records/server/crud";
import {
  GridConfiguration,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  vsObjectName as gamingContractMachineName,
  vsMachineColumnSetName as machineColumnSetName,
  vsContractColumnSetName as contractColumnSetName,
} from "./configs/gamingContractMachine";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
  IGridSettings,
} from "../../../records/grids/settings/gridSettingsService";
import {
  MachineDetailTabsProvider,
  vsProviderName as machineDetailTabsProviderName,
} from "../../shared/components/views/machines/detail/tabs";
import {
  vsComponentName as configurableGridFormViewComponentName,
  ConfigurableGridFormViewConfig,
} from "../../../records/views/configurableGridFormView";
import { GridSelectionChangedArgs } from "../../../records/grids/controller";
import { IQuery, ServiceResponse } from "../../../records/server/query";
import {
  vsServiceName as ConfigurableGridFormViewServiceName,
  ConfigurableGridFormViewService,
} from "../../../records/views/configurableGridFormView/service";
import { Machine } from "../../shared/models/machine";
import { MachineConfiguration } from "../../shared/models/machineConfiguration";
import {
  ColumnDefinitionSetService,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import { ColDef } from "../../../records/grids/columns/colDef";

const sensitiveInfoPermission = "contractSensitiveInfo";



export function addContractTabToMachineDetail(config: {
  machineDetailsTabsProvider: MachineDetailTabsProvider;
  displayName: string;
  id: string;
  uiSrefFragment: string;
  contractTypeId: string;
  gridEditSymbolKey: string;
  gridViewSymbolKey: string;
}) {
  const {
    machineDetailsTabsProvider,
    displayName,
    id,
    uiSrefFragment,
    contractTypeId,
    gridEditSymbolKey,
    gridViewSymbolKey,
  } = config;

  machineDetailsTabsProvider.addElement({
    displayName,
    id,
    uiSrefFragment,
    requiredPermission: "contractView",
    stateDefinition: {
      url: `/${uiSrefFragment}`,
      component: configurableGridFormViewComponentName,
      resolve: {
        defaultQuery: [
          "machine",
          (machine: any) => ({ MachineId: machine.Id }),
        ],
        config: [
          RecordGridViewServiceName,
          GridSettingsServiceName,
          "machine",
          ColumnDefinitionSetServiceName,
          ColumnDefinitionServiceName,
          ContractServiceName,
          (
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService,
            machine: Machine<MachineConfiguration>,
            columnDefinitionSetService: ColumnDefinitionSetService,
            columnDefinitionService: ColumnDefinitionService,
            contractService: ContractService,
          ): ConfigurableGridFormViewConfig =>
            getConfigurableGridFormViewConfigForContractMachines(
              recordGridViewService,
              gridSettingsService.getGridSettings(Symbol(gridViewSymbolKey)),
              { MachineId: machine.Id },
              columnDefinitionSetService.getColumnDefinitionSet(contractColumnSetName),
              columnDefinitionService,
              contractService
            ),
        ],
      },
    },
    children: [
      {
        displayName: "Edit Contracts",
        id: "edit",
        requiredPermission: "contractEdit",
        uiSrefFragment: "edit",
        stateDefinition: {
          url: "/edit",
          views: {
            "@^.^": {
              component: configurableGridFormViewComponentName,
            },
          },
          resolve: {
            selectedIds: [
              ServerUtilityName,
              RecordGridViewServiceName,
              "machine",
              (
                serverUtility: ServerUtility,
                recordGridViewService: RecordGridViewService,
                machine: Record
              ) => {
                const gridConfig = recordGridViewService.getGridConfiguration(
                  gamingContractMachineName
                )!;
                return serverUtility
                  .fetchAll<Record>({
                    url: gridConfig.gridDataSource!.url,
                    query: { MachineId: machine.Id },
                  })
                  .then((result) => result.map((r) => r.ContractInstanceId));
              },
            ],
            gridConfiguration: [
              RecordGridViewServiceName,
              GridSettingsServiceName,
              (
                recordGridViewService: RecordGridViewService,
                gridSettingsService: GridSettingsService
              ): GridConfiguration => {
                const config = recordGridViewService.getGridConfiguration(contractTypeId)!;
                config.gridSettings = gridSettingsService.getGridSettings(Symbol(gridEditSymbolKey));
                return config;
              },
            ],
            config: [
              ConfigurableGridFormViewServiceName,
              ServerUtilityName,
              "selectedIds",
              "machine",
              "gridConfiguration",
              (
                configurableGridFormViewService: ConfigurableGridFormViewService,
                serverUtility: ServerUtility,
                selectedIds: number[],
                machine: Record,
                gridConfiguration: GridConfiguration
              ): ConfigurableGridFormViewConfig =>
                getConfigurationGridEditViewConfigForContractMachines({
                  configurableGridFormViewService,
                  gridConfiguration,
                  selectedIds,
                  gridSelectionChanged: (args: GridSelectionChangedArgs) => {
                    const contractId = args.data.Id;
                    const machineId = machine.Id;
                    setGamingContractMachine(serverUtility, machineId, contractId, args.selected);
                  },
                }),
            ],
          },
        },
      },
    ],
  });
}


function setGamingContractMachine(
  serverUtility: ServerUtility,
  machineId: number,
  contractId: number,
  setSelection: boolean
) {
  const url = "/api/contract/machines";

  setSelection
    ? serverUtility.post({
        url,
        postObject: {
          ContractInstanceId: contractId,
          MachineId: machineId,
        },
      })
    : serverUtility.sendApiRequest<ServiceResponse<Record>>({
        url: url + `/${contractId}/byMachineId`,
        method: "delete",
        data: {
          MachineId: machineId,
        },
      });
}

function getConfigurationGridEditViewConfigForContractMachines(config: {
  configurableGridFormViewService: ConfigurableGridFormViewService;
  selectedIds: number[];
  gridConfiguration: GridConfiguration;
  gridSelectionChanged: (args: GridSelectionChangedArgs) => void;
}): ConfigurableGridFormViewConfig {
  const {
    selectedIds,
    gridConfiguration,
    gridSelectionChanged,
    configurableGridFormViewService,
  } = config;

  return {
    defaultQuery: {
      Ids: selectedIds.length > 0 ? selectedIds : [0],
    },
    gridConfiguration,
    additionalToggleButtons: [
      configurableGridFormViewService.idDefaultQueryToggleButton(true),
    ],
    showNavButton: true,
    navButtonIcon: "fa-pencil",
    navButtonPermission: undefined,
    navButtonText: "Done",
    navPath: "^",
    selectedIds,
    showSelectButtons: true,
    gridSelectionChanged: (args: GridSelectionChangedArgs) => {
      gridSelectionChanged(args);
    },
  };
}

function getConfigurableGridFormViewConfigForContractMachines(
  recordGridViewService: RecordGridViewService,
  gridSettings: IGridSettings,
  defaultQuery: IQuery,
  columns: ColDef[],
  columnDefinitionService: ColumnDefinitionService,
  contractService: ContractService,
): ConfigurableGridFormViewConfig {
  const contractMachineGridConfiguration =
    recordGridViewService.getGridConfiguration(gamingContractMachineName)!;

  contractMachineGridConfiguration.gridSettings = gridSettings;

  const infoColumn: ColDef = columnDefinitionService.getLinkColDef({
    colId: "info",
    headerName: "Info",
    iconClass: "fa fa-info-circle text-primary",
    stateExpression: "main.contract.type.info.redirect({ type: data.ContractInstanceType, contractInstanceId: data.ContractInstanceId})",
  });
  const ColumnsWithInfo = [...columns, infoColumn];


  contractMachineGridConfiguration.columns = ColumnsWithInfo;

  return {
    defaultQuery,
    gridConfiguration: contractMachineGridConfiguration,
    additionalToggleButtons: [],
    navButtonIcon: "fa-pencil",
    navButtonPermission: "contractEdit",
    navButtonText: "Edit",
    navPath: ".edit",
    showNavButton: true,
    showSelectButtons: false,
  };
}
